import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Card, Descriptions, Tag, Button, Spin, Alert, Typography,
  Row, Col, Progress, message, Grid,
} from 'antd'
import {
  ArrowLeftOutlined,
} from '@ant-design/icons'
import { dashboardAPI, RequestDetails as RequestDetailsType } from '../services/api'
import LLMNarrativeCard, { LLMNarrative } from '../components/LLMNarrativeCard'
import ValidationCollapsePanel from '../components/ValidationCollapsePanel'

const { Title, Text } = Typography
const { useBreakpoint } = Grid

const getDecisionStatusColor = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'APPROVED': return 'success'
    case 'REJECTED': return 'error'
    default: return 'warning'
  }
}

const RequestDetails: React.FC = () => {
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [requestDetails, setRequestDetails] = useState<RequestDetailsType | null>(null)

  useEffect(() => {
    const loadRequestDetails = async () => {
      if (!id) return
      try {
        setLoading(true)
        const response = await dashboardAPI.getRequestDetails(id)
        setRequestDetails({
          ...response.request,
          validations: response.validations,
        })
      } catch (error) {
        message.error('Error al cargar los detalles de la solicitud')
        console.error('Request details load error:', error)
      } finally {
        setLoading(false)
      }
    }

    loadRequestDetails()
  }, [id])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!requestDetails) {
    return (
      <div>
        <Alert
          message="Solicitud no encontrada"
          description="La solicitud que busca no existe o ha sido eliminada."
          type="error"
          showIcon
          action={
            <Button onClick={() => navigate('/requests')}>
              Volver a Solicitudes
            </Button>
          }
        />
      </div>
    )
  }

  const validations = requestDetails.validations || {}
  const validationEntries = Object.entries(validations).filter(
    ([key]) => key !== 'ai_model' && key !== 'llm_narrative'
  )
  const passedCount = validationEntries.filter(([, v]) => v.status?.toUpperCase() === 'PASSED').length
  const totalCount = validationEntries.length
  const validationProgress = totalCount > 0 ? (passedCount / totalCount) * 100 : 0

  const llmNarrativeRaw = validations['llm_narrative']
  const llmNarrative: LLMNarrative | null =
    llmNarrativeRaw?.details && typeof llmNarrativeRaw.details === 'object'
      ? (llmNarrativeRaw.details as LLMNarrative)
      : null

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20, gap: 8, flexWrap: 'wrap' }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/requests')}
          style={{ marginRight: 16 }}
        >
          Volver
        </Button>
        <Title level={2} style={{ margin: 0, fontSize: isMobile ? '22px' : undefined }}>
          Detalles de Solicitud {requestDetails.simulation_id}
        </Title>
        <Tag
          color={getDecisionStatusColor(requestDetails.decision_status)}
          style={{ marginLeft: isMobile ? 0 : 16, fontSize: '14px', padding: '4px 12px' }}
        >
          {requestDetails.decision_status}
        </Tag>
      </div>

      <Row gutter={[16, 16]}>
        {/* Información General */}
        <Col xs={24} lg={12}>
          <Card title="Información General" style={{ marginBottom: 24 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="ID Simulación">
                <Text code>{requestDetails.simulation_id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Estado de Decisión">
                <Tag color={getDecisionStatusColor(requestDetails.decision_status)}>
                  {requestDetails.decision_status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Razón de Decisión">
                <Text>{requestDetails.decision_reason}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Tipo de Cliente">
                <Text>{requestDetails.customer_type || 'N/A'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Score Crediticio">
                <Text strong>{requestDetails.credit_score || 'N/A'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Creación">
                <Text>{new Date(requestDetails.created_at).toLocaleString('es-CO')}</Text>
              </Descriptions.Item>
              {requestDetails.updated_at && (
                <Descriptions.Item label="Última Actualización">
                  <Text>{new Date(requestDetails.updated_at).toLocaleString('es-CO')}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>

        {/* Análisis de IA */}
        <Col xs={24} lg={12}>
          <Card title="Análisis de IA" style={{ marginBottom: 24 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Recomendación IA">
                <Tag color={getDecisionStatusColor(requestDetails.ai_recommendation || '')}>
                  {requestDetails.ai_recommendation || 'N/A'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Nivel de Confianza">
                {requestDetails.ai_confidence ? (
                  <Progress
                    percent={Math.round(requestDetails.ai_confidence * 100)}
                    size="small"
                    style={{ marginTop: 4 }}
                    strokeColor={
                      requestDetails.ai_confidence > 0.8 ? '#52c41a' :
                      requestDetails.ai_confidence > 0.6 ? '#faad14' : '#ff4d4f'
                    }
                  />
                ) : 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Progreso de Validaciones */}
          <Card title="Progreso de Validaciones" style={{ marginBottom: 24 }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Text strong>
                {passedCount} de {totalCount} validaciones aprobadas
              </Text>
            </div>
            <Progress
              percent={Math.round(validationProgress)}
              size="default"
              strokeColor={validationProgress === 100 ? '#52c41a' : '#1890ff'}
            />
          </Card>
        </Col>
      </Row>

      {/* Análisis Asistido por IA */}
      {llmNarrative && (
        <LLMNarrativeCard
          narrative={llmNarrative}
          decision={requestDetails.decision_status}
        />
      )}

      {/* Validaciones Realizadas */}
      <Card title="🔍 Validaciones Realizadas" style={{ marginBottom: 24 }}>
        {validationEntries.length > 0 ? (
          <ValidationCollapsePanel validations={validations} />
        ) : (
          <Alert
            message="No hay validaciones disponibles"
            description="Esta solicitud no tiene información de validaciones registrada."
            type="info"
            showIcon
          />
        )}
      </Card>

      {/* Datos Técnicos */}
      <Row gutter={[16, 16]}>
        {requestDetails.experian_data && (
          <Col xs={24} xl={12}>
            <Card title="Datos Experian" size="small">
              <pre style={{ fontSize: '11px', overflow: 'auto', maxHeight: '300px' }}>
                {JSON.stringify(requestDetails.experian_data, null, 2)}
              </pre>
            </Card>
          </Col>
        )}

        {requestDetails.truora_data && (
          <Col xs={24} xl={12}>
            <Card title="Datos Truora" size="small">
              <pre style={{ fontSize: '11px', overflow: 'auto', maxHeight: '300px' }}>
                {JSON.stringify(requestDetails.truora_data, null, 2)}
              </pre>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  )
}

export default RequestDetails
